import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, FolderKanban, Plus, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/project/new", label: "New Project", icon: Plus, testid: "nav-new-project" },
  { to: "/brand-settings", label: "Studio Settings", icon: Settings, testid: "nav-settings" },
];

export default function StudioShell({ children, projectContext }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r" style={{ borderColor: "var(--border)", background: "#fff" }}>
        <div className="px-6 py-8 border-b" style={{ borderColor: "var(--border)" }}>
          <Link to="/" className="block" data-testid="brand-mark">
            <div className="eyebrow mb-2">Atelier</div>
            <div className="font-serif-editorial text-2xl leading-none">Moodboard Narrators</div>
          </Link>
        </div>
        <nav className="flex-1 py-6">
          {nav.map(({ to, label, icon: Icon, testid }) => {
            const active = loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
            return (
              <Link key={to} to={to} data-testid={testid} className={`flex items-center gap-3 px-6 py-3 text-sm hover-underline ${active ? "text-black font-medium" : "text-stone-500"}`}>
                <Icon size={16} strokeWidth={1.5} />
                <span>{label}</span>
              </Link>
            );
          })}
          {projectContext && (
            <div className="mt-8 px-6">
              <div className="eyebrow mb-3">Current Project</div>
              <div className="text-sm font-medium truncate">{projectContext.name}</div>
              <div className="text-xs text-stone-500 mt-1">{projectContext.client_name}</div>
              <div className="mt-4 space-y-2">
                {[
                  ["/project/"+projectContext.id+"/edit","Project details"],
                  ["/project/"+projectContext.id+"/analysis","Analysis"],
                  ["/project/"+projectContext.id+"/directions","Directions"],
                  ["/project/"+projectContext.id+"/dna","Design DNA"],
                  ["/project/"+projectContext.id+"/moodboards","Mood Boards"],
                  ["/project/"+projectContext.id+"/presentation","Presentation"],
                ].map(([p,l]) => (
                  <Link key={p} to={p} className={`block text-xs py-1 ${loc.pathname===p?"text-black font-medium":"text-stone-500 hover:text-black"}`} data-testid={`project-nav-${l.toLowerCase().replace(/ /g,'-')}`}>{l}</Link>
                ))}
              </div>
            </div>
          )}
        </nav>
        <div className="px-6 py-6 border-t flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid="shell-user">
          {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" /> : <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-sm font-serif-editorial">{(user?.name || "?")[0]}</div>}
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate" data-testid="shell-user-name">{user?.name}</div>
            <div className="text-[11px] text-stone-500 truncate">{user?.email}</div>
          </div>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="text-stone-400 hover:text-black" title="Sign out" data-testid="logout-btn"><LogOut size={16} /></button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
